// Guided tours: a spotlight on one element at a time and a card explaining it.
// A step can wait for the player to act (click the highlighted element, or any condition) before moving on,
// so the player learns by doing. Tours are data (tours.ts); texts come from the dictionary (i18n/<lang>/tour.ts).
import { esc } from '../dom.js';
import { t } from '../../i18n/index.js';
import { icon } from '../brand.js';
import { sfx } from '../../audio/sfx.js';
import { withKeys } from './keys.js';

export interface TourStep {
  id: string;
  /** Screen to show first (hash route, e.g. '/play'). */
  route?: string;
  /** Element to highlight: selector or function. None: card centred on screen. */
  target?: string | (() => Element | null);
  /** Interactive: 'click' waits for a click on the target; a function waits for the condition. */
  wait?: 'click' | (() => boolean);
  /** Preferred side of the card. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Extra space around the highlighted element (px). */
  pad?: number;
  /** Runs when the step starts (open a tab, scroll, prepare a state). */
  before?: () => void;
  /** The element may legitimately be absent (a card shown only in some modes): the check does not require it. */
  optional?: boolean;
}
export interface TourDef { id: string; icon: string; route: string; steps: TourStep[]; onDone?: () => void }

interface Running { def: TourDef; i: number; root: HTMLElement; raf: number; cleanup: (() => void)[]; token: number }
let run: Running | null = null;
const listeners = new Set<(id: string, done: boolean) => void>();
/** Notified when a tour ends (finished or closed), to remember progress. */
export const onTourEnd = (fn: (id: string, done: boolean) => void) => { listeners.add(fn); return () => listeners.delete(fn); };
export const tourRunning = () => !!run;

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const find = (s: TourStep): Element | null => {
  if (!s.target) return null;
  const el = typeof s.target === 'function' ? s.target() : document.querySelector(s.target);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? el : null;
};
/** Texts of a step: [title, body]. */
const text = (tour: string, step: string): [string, string] => {
  const T = t.tour.steps as Record<string, Record<string, readonly string[]>>;
  const v = T[tour]?.[step];
  return v ? [v[0], v[1]] : [step, ''];
};

/**
 * Replays a tour without showing it: goes through its screens, runs each step's preparation, clicks where a step
 * waits for a click, and returns the steps whose element cannot be found. Used by the e2e journey, so a screen
 * that changes without its tour fails the build instead of showing a card in the void.
 */
export async function checkTour(def: TourDef, waitMs = 8000): Promise<string[]> {
  const missing: string[] = [];
  for (const s of def.steps) {
    if (s.route && location.hash.slice(1) !== s.route) location.hash = s.route;
    s.before?.();
    if (!s.target) continue;
    let el = find(s);
    for (let k = 0; k < waitMs / 100 && !el; k++) { await sleep(100); el = find(s); }
    if (!el) { if (!s.optional) missing.push(`${def.id}.${s.id}`); continue; }
    if (s.wait === 'click') { (el as HTMLElement).click(); await sleep(400); }
  }
  return missing;
}

export function startTour(def: TourDef, from = 0) {
  stopTour(false);
  const root = document.createElement('div');
  root.className = 'tour';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-live', 'polite');
  // four blockers around the spotlight: the rest of the screen cannot be clicked during a tour
  root.innerHTML = `<div class="tour-block" data-b="t"></div><div class="tour-block" data-b="r"></div><div class="tour-block" data-b="b"></div><div class="tour-block" data-b="l"></div>
    <div class="tour-spot"></div><div class="tour-card"></div>`;
  document.body.appendChild(root);
  run = { def, i: -1, root, raf: 0, cleanup: [], token: 0 };
  const key = (e: KeyboardEvent) => {
    if (!run) return;
    // Escape closes the tour only, not the modal it may be showing
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); stopTour(false); }
    else if (e.key === 'ArrowRight' && !run.def.steps[run.i]?.wait) { e.preventDefault(); go(run.i + 1); }
    else if (e.key === 'ArrowLeft' && run.i > 0) { e.preventDefault(); go(run.i - 1); }
  };
  document.addEventListener('keydown', key, true);
  root.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest('[data-tour-act]') as HTMLElement | null;
    if (!b || !run) return;
    const a = b.dataset.tourAct;
    if (a === 'next') go(run.i + 1);
    else if (a === 'back') go(run.i - 1);
    else if (a === 'close') stopTour(false);
  });
  run.cleanup.push(() => document.removeEventListener('keydown', key, true));
  const follow = () => { if (!run) return; place(); run.raf = requestAnimationFrame(follow); };
  run.raf = requestAnimationFrame(follow);
  go(from);
}

export function stopTour(done: boolean) {
  if (!run) return;
  const r = run;
  run = null;
  cancelAnimationFrame(r.raf);
  r.cleanup.forEach(f => f());
  stepCleanup.forEach(f => f()); stepCleanup = [];
  r.root.classList.add('out');
  setTimeout(() => r.root.remove(), 220);
  if (done) r.def.onDone?.();
  listeners.forEach(f => f(r.def.id, done));
}

let stepCleanup: (() => void)[] = [];
async function go(i: number) {
  if (!run) return;
  const R = run, def = R.def;
  if (i >= def.steps.length) { sfx('levelup'); stopTour(true); return; }
  if (i < 0) return;
  stepCleanup.forEach(f => f()); stepCleanup = [];
  const token = ++R.token, s = def.steps[i];
  R.i = i;
  R.root.classList.add('moving');
  if (s.route && location.hash.slice(1) !== s.route) location.hash = s.route;
  s.before?.();
  // the screen may still be drawing: wait for the element (at most 4 s), otherwise show the card in the centre
  let el = find(s);
  for (let k = 0; k < 40 && s.target && !el; k++) { await sleep(100); if (!run || R.token !== token) return; el = find(s); }
  if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); await sleep(260); if (!run || R.token !== token) return; }
  R.root.classList.remove('moving');
  card(s, i);
  if (s.wait === 'click' && el) {
    const h = () => { setTimeout(() => { if (run && R.token === token) go(i + 1); }, 350); };
    el.addEventListener('click', h, { once: true, capture: true });
    stepCleanup.push(() => el!.removeEventListener('click', h, true));
  } else if (typeof s.wait === 'function') {
    const cond = s.wait;
    const iv = setInterval(() => { try { if (cond()) { clearInterval(iv); sfx('lap'); if (run && R.token === token) go(i + 1); } } catch { /* screen redrawn */ } }, 200);
    stepCleanup.push(() => clearInterval(iv));
  }
}

function card(s: TourStep, i: number) {
  if (!run) return;
  const R = run, T = t.tour, n = R.def.steps.length, [title, body] = text(R.def.id, s.id);
  const c = R.root.querySelector('.tour-card') as HTMLElement;
  const waiting = !!s.wait;
  c.innerHTML = `<button class="ghost icon tour-x" data-tour-act="close" aria-label="${esc(T.close)}">${icon('x', 16)}</button>
    <div class="tour-kick">${esc(T.tours[R.def.id as keyof typeof T.tours]?.title || '')} <span>${i + 1} / ${n}</span></div>
    <h3>${esc(title)}</h3><p>${withKeys(body)}</p>
    ${waiting ? `<div class="tour-do"><span class="tour-pulse"></span>${esc(s.wait === 'click' ? T.doClick : T.doAction)}</div>` : ''}
    <div class="tour-foot"><div class="tour-dots">${Array.from({ length: n }, (_, k) => `<i class="${k === i ? 'on' : k < i ? 'past' : ''}"></i>`).join('')}</div><span class="spacer"></span>
      ${i > 0 ? `<button class="ghost" data-tour-act="back">${esc(T.back)}</button>` : ''}
      ${waiting ? `<button class="ghost" data-tour-act="next">${esc(T.skipStep)}</button>` : `<button class="primary" data-tour-act="next">${esc(i === n - 1 ? T.finish : T.next)}</button>`}</div>`;
  c.classList.remove('pop'); void c.offsetWidth; c.classList.add('pop');
  R.root.classList.toggle('interactive', s.wait === 'click');
  (c.querySelector('[data-tour-act="next"]') as HTMLElement | null)?.focus({ preventScroll: true });
  place();
}

/** Spotlight, blockers and card follow the element (scrolling, resizing, screen redrawn). */
function place() {
  if (!run) return;
  const R = run, s = R.def.steps[R.i];
  if (!s) return;
  const el = find(s), vw = innerWidth, vh = innerHeight, pad = s.pad ?? 8;
  const spot = R.root.querySelector('.tour-spot') as HTMLElement, c = R.root.querySelector('.tour-card') as HTMLElement;
  const b = (k: string) => R.root.querySelector(`[data-b="${k}"]`) as HTMLElement;
  let x = vw / 2, y = vh / 2, w = 0, h = 0;
  if (el) { const r = el.getBoundingClientRect(); x = r.left - pad; y = r.top - pad; w = r.width + pad * 2; h = r.height + pad * 2; }
  Object.assign(spot.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px`, opacity: el ? '1' : '0' });
  R.root.classList.toggle('centered', !el);
  // blockers: everything except the hole (the hole only accepts clicks on interactive steps)
  Object.assign(b('t').style, { left: '0', top: '0', width: `${vw}px`, height: `${Math.max(0, y)}px` });
  Object.assign(b('b').style, { left: '0', top: `${y + h}px`, width: `${vw}px`, height: `${Math.max(0, vh - y - h)}px` });
  Object.assign(b('l').style, { left: '0', top: `${y}px`, width: `${Math.max(0, x)}px`, height: `${h}px` });
  Object.assign(b('r').style, { left: `${x + w}px`, top: `${y}px`, width: `${Math.max(0, vw - x - w)}px`, height: `${h}px` });
  spot.style.pointerEvents = s.wait === 'click' ? 'none' : 'auto';
  // card: on the preferred side if it fits, else the side with the most room; centred when nothing is highlighted
  const cw = Math.min(380, vw - 32), ch = c.offsetHeight || 220, gap = 16;
  let cx: number, cy: number;
  if (!el) { cx = (vw - cw) / 2; cy = (vh - ch) / 2; }
  else {
    const room = { bottom: vh - (y + h), top: y, right: vw - (x + w), left: x };
    const fits = (k: keyof typeof room) => (k === 'bottom' || k === 'top' ? room[k] > ch + gap : room[k] > cw + gap);
    const order = [s.side, 'bottom', 'right', 'top', 'left'].filter(Boolean) as (keyof typeof room)[];
    const side = order.find(fits) || 'bottom';
    if (side === 'bottom' || side === 'top') { cx = x + w / 2 - cw / 2; cy = side === 'bottom' ? y + h + gap : y - ch - gap; }
    else { cy = y + h / 2 - ch / 2; cx = side === 'right' ? x + w + gap : x - cw - gap; }
    // a target taller than the screen: the card stays inside it, at the bottom
    if (!fits(side)) { cx = Math.min(vw - cw - 16, Math.max(16, x + w - cw - 16)); cy = vh - ch - 24; }
    c.dataset.side = side;
  }
  Object.assign(c.style, { width: `${cw}px`, left: `${Math.round(Math.min(vw - cw - 12, Math.max(12, cx)))}px`, top: `${Math.round(Math.min(vh - ch - 12, Math.max(12, cy)))}px` });
}
